import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { IonicModule } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Landing page for an Auth0 Organization invitation.
 *
 * Auth0 builds the `invitation_url` in the invite mail from the application's
 * "Application Login URI" and appends `?invitation=…&organization=org_…&organization_name=…`.
 * Those parameters are the *only* way to redeem the invitation: they have to be handed to
 * `loginWithRedirect` as authorization params. Before this page existed the invitee simply
 * landed on `/home` with the parameters sitting unused in the address bar, had to find the
 * login button themselves, and the plain login that followed carried no invitation — so the
 * membership was never established and the mail effectively did nothing.
 *
 * The page shows who invited them, then forwards to Auth0 on its own after a short beat
 * (INVITE_AUTO_REDIRECT_MS) so the flow needs no click, with an explicit button as the
 * fallback for anyone who lands here with JS timing/back-button oddities.
 *
 * Set the Auth0 application's Application Login URI to `<origin>/invite` so invitees arrive
 * here directly; AppComponent additionally forwards any other route that receives the
 * parameters, so an app still pointing at `/home` keeps working.
 */
@Component({
    selector: 'app-invite',
    templateUrl: './invite.page.html',
    styleUrls: ['./invite.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, TranslatePipe],
})
export class InvitePage implements OnInit, OnDestroy {
  /** Delay before the automatic redirect, long enough to read the card without feeling stuck. */
  private static readonly INVITE_AUTO_REDIRECT_MS = 1200;

  organizationName = '';
  /** True when the link carries no usable invitation (expired mail re-opened, hand-typed URL). */
  invalidLink = false;
  redirecting = false;

  private invitation: string | null = null;
  private organization: string | null = null;
  private timer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly auth0: AuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.invitation = params.get('invitation');
    this.organization = params.get('organization');
    this.organizationName = params.get('organization_name') ?? '';

    if (!this.invitation || !this.organization) {
      this.invalidLink = true;
      this.cdr.markForCheck();
      return;
    }

    this.timer = setTimeout(() => this.accept(), InvitePage.INVITE_AUTO_REDIRECT_MS);
  }

  ngOnDestroy(): void {
    if (this.timer) clearTimeout(this.timer);
  }

  /**
   * Hands the invitation ticket to Auth0. Auth0 resolves it to the invited email address and
   * takes the user straight to sign-up (or sign-in, if that address already has an account),
   * then returns to /callback, which routes a brand-new user on to /onboarding.
   */
  accept(): void {
    if (!this.invitation || !this.organization || this.redirecting) return;
    this.redirecting = true;
    this.cdr.markForCheck();

    this.auth0.loginWithRedirect({
      authorizationParams: {
        invitation: this.invitation,
        organization: this.organization,
        redirect_uri: `${window.location.origin}/callback`,
      },
    });
  }
}
